/*
This file is located in the main branch
Last time the file was updated (UTC): [02.07.2024 18:53:01]
Permissions to update outside the main branch: [false]
*/

import { Button, DropDown, Form, Modal } from '@finbeat/react-toolkit';
import { AxiosError } from 'axios';
import { getSelectList } from 'axios/dictionaryApi';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { AxiosErrorResponse } from 'models/api/axiosErrorResponse';
import { PrescrItem } from 'models/api/prescr';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import 'scss/Main.scss';
import { usePrescrWorkplaceState } from './Prescr';
import PrescrModalContent from './PrescrModalContent';

type BrandsChangeModalProps = {
    isOpen?: boolean;
    toggle: Dispatch<SetStateAction<boolean>>;
};

const PrescrModal = ({ isOpen = false, toggle }: BrandsChangeModalProps) => {
    const { t } = useTranslation();
    const api = useApi();
    const [prescrRuleTypes, setPrescrRuleTypes] = useState<DropDown[]>([]);
    const [coverageTypes, setCoverageTypes] = useState<DropDown[]>([]);

    const { hasRole } = useAuth();

    const { changeItemMutation, addItemMutation, currentItem, handleError } =
        usePrescrWorkplaceState();

    const { isFetching: dictsLoading } = useQuery(
        'prescrModalDicts',
        () => getSelectList(api, ['PrescrRuleTypes', 'CoverageTypes']),
        {
            onSuccess: (data) => {
                if (data) {
                    data['PrescrRuleTypes'] && setPrescrRuleTypes(data['PrescrRuleTypes']);
                    data['CoverageTypes'] && setCoverageTypes(data['CoverageTypes']);
                }
            },
            onError: (err: AxiosError<AxiosErrorResponse>) => {
                handleError(err.response?.data);
            },
        }
    );

    const defaultItem: PrescrItem = useMemo(
        () => ({
            id: undefined,
            isActive: false,
            type: prescrRuleTypes[0]?.id?.toString() ?? '',
            tradeOrgCoverage: coverageTypes[0]?.id?.toString() ?? '',
            tradeOrgs: [],
            priority: null,
        }),
        [prescrRuleTypes, coverageTypes]
    );

    const defaultValues = useMemo(() => currentItem ?? defaultItem, [currentItem, defaultItem]);

    const saveChanges = async (item: PrescrItem) => {
        item.id ? changeItemMutation.mutate(item) : addItemMutation.mutate(item);

        toggle(false);
    };

    const formHasRole = (): boolean =>
        currentItem ? hasRole('prescrCreate') : hasRole('prescrUpdate');

    return (
        <Modal
            size="lg"
            isOpen={isOpen}
            toggle={() => toggle(false)}
            scrollable
            className="brands__modal"
            data-testid="prescr-modal"
        >
            <Form<PrescrItem>
                onSubmit={saveChanges}
                defaultValues={defaultValues}
                className="form-in-modal"
            >
                <ModalHeader toggle={() => toggle(false)}>
                    {defaultValues.id ? `#${defaultValues.id}` : t('BackOfficeNew')}
                </ModalHeader>
                <ModalBody className="custom-scrollbar">
                    <PrescrModalContent
                        prescrRuleTypes={prescrRuleTypes}
                        coverageTypes={coverageTypes}
                        dictsLoading={dictsLoading}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => toggle(false)}>
                        <Trans i18nKey="BackOfficeCancel" />
                    </Button>
                    <Button color="success" type="submit" disabled={!formHasRole()}>
                        <Trans i18nKey="BackOfficeSave" />
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default PrescrModal;
